/* import __COLOCATED_TEMPLATE__ from './scim-warning.hbs'; */
/* RESPONSIBLE TEAM: team-app-security */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type AppService from 'embercom/services/app-service';
import ajax from 'embercom/lib/ajax';

interface Signature {
  Args: {
    permissionsObject: {
      id?: string;
    };
    hasChanges?: boolean;
    app?: {
      id?: string;
    };
    adminId?: string;
  };
  Element: HTMLDivElement;
}

interface ScimGroup {
  id: number;
  name: string;
  priority?: number;
}

export default class ScimWarning extends Component<Signature> {
  @service declare scimGroupService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare appService: AppService;

  @tracked scimGroup: ScimGroup | null = null;
  @tracked isLoadingGroup = false;
  @tracked hasLoadingError = false;
  @tracked assignedViaScim = false;
  @tracked isLoadingAssignmentStatus = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.loadScimAssignmentStatus();
  }

  async loadScimAssignmentStatus() {
    if (!this.args.permissionsObject?.id) {
      return;
    }

    this.isLoadingAssignmentStatus = true;

    try {
      let result = await ajax({
        url: `/ember/permissions/${this.args.permissionsObject.id}/scim_assignment_status`,
        type: 'GET',
        data: {
          app_id: this.appService.app.id,
        },
      });

      let isAssignedViaScim = result.assigned_via_scim;

      if (isAssignedViaScim) {
        await this.loadScimGroup();
      }

      this.assignedViaScim = isAssignedViaScim;
    } catch (error) {
      console.error('Failed to fetch SCIM assignment status:', error);
      this.assignedViaScim = false;
    } finally {
      this.isLoadingAssignmentStatus = false;
    }
  }

  async loadScimGroup() {
    if (!this.args.app?.id || !this.args.adminId) {
      return;
    }

    this.isLoadingGroup = true;
    this.hasLoadingError = false;

    try {
      let result = await this.scimGroupService.fetchUserGroups.perform(
        this.args.app.id,
        this.args.adminId,
      );

      if (Array.isArray(result?.groups)) {
        this.scimGroup = this.scimGroupService.getTopPriorityGroup(result.groups);
      } else {
        console.warn('SCIM group service returned invalid or empty data:', result);
        this.hasLoadingError = true;
      }
    } catch (error) {
      console.warn('Failed to load SCIM group information:', error);
      this.hasLoadingError = true;
    } finally {
      this.isLoadingGroup = false;
    }
  }

  get containerClasses() {
    return this.args.hasChanges
      ? 'bg-error-container border-error-border'
      : 'bg-notice-container border-notice-border';
  }

  get iconColor() {
    return this.args.hasChanges ? 'text-error' : 'notice-fill';
  }

  get textClasses() {
    return this.args.hasChanges ? 'text-error' : 'text-notice';
  }

  get messageKey() {
    return this.scimGroup?.name && !this.hasLoadingError
      ? 'settings.teammates.permissions.scim-managed-alert-with-group'
      : 'settings.teammates.permissions.scim-managed-alert';
  }

  get translationOptions() {
    return this.scimGroup?.name && !this.hasLoadingError ? { groupName: this.scimGroup.name } : {};
  }

  get shouldShowWarning() {
    return this.assignedViaScim && !this.isLoadingGroup;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Settings::Teammates::Permissions::ScimWarning': typeof ScimWarning;
    'settings/teammates/permissions/scim-warning': typeof ScimWarning;
  }
}
